import React from 'react'
import { 
  Box, 
  Typography, 
  Button, 
  Container,
  useTheme,
  useMediaQuery,
  Card,
  CardMedia
} from '@mui/material'
import { useHandleChangeRoute } from 'data/hooks'
import BackgroundVideo from 'static/videos/mystethi-video-overlay1.mp4'
import BackgroundVideoMobile from 'static/videos/mystethi-video-overlay_mobile.mp4'

const NewHeroSection = () => {
  const theme = useTheme()
  const below_sm = useMediaQuery(theme.breakpoints.down('sm'))
  const handleChangeRoute = useHandleChangeRoute()
  const video = below_sm ? BackgroundVideoMobile : BackgroundVideo

  return (
    <Card
      sx={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        minHeight: '100vh',
        borderRadius: 0,
        boxShadow: 'none',
        backgroundColor: theme.palette.grey[800] // Fallback background color when video doesn't load
      }}
    >
      {/* Video Background */}
      <CardMedia 
        component='video' 
        src={video} 
        autoPlay 
        muted 
        loop 
        playsInline
        preload="auto"
        title="MyStethi healthcare job platform demonstration video"
        aria-label="Background video showing MyStethi's medical job search platform in action"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0
        }}
        onError={(e) => console.error('Video failed to load:', e)}
      />
      
      {/* Overlay for better text readability */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: `${theme.palette.grey[800]}66`, // Using theme color with opacity
          zIndex: 1
        }}
      />

      {/* Content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '80vh',
            textAlign: 'center'
          }}
        >
          {/* Main Content */}
          <Box sx={{ maxWidth: '800px', zIndex: 1 }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                fontWeight: 700,
                color: 'white',
                lineHeight: 1.2,
                mb: 3,
                textAlign: 'center',
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
              }}
            >
              Search Smarter for Medical Jobs
            </Typography>

            <Typography
              variant="h2"
              sx={{
                color: 'white',
                opacity: 0.9,
                mb: 4,
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                fontWeight: 400,
                lineHeight: 1.6,
                textAlign: 'center',
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
              }}
            >
              From frustration to innovation: job search tools crafted by clinicians, for clinicians.
            </Typography>



            {/* Call to Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 2, sm: 3 },
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                justifyContent: 'center',
                mt: { xs: 3, sm: 2 }
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => handleChangeRoute('/register/physicians')()}
                sx={{
                  borderRadius: '50px',
                  px: { xs: 4, sm: 5 },
                  py: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '1rem', sm: '1.2rem' },
                  fontWeight: 600,
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  boxShadow: theme.palette.shadows.colored.primary,
                  border: `2px solid ${theme.palette.primary.main}`,
                  textTransform: 'none',
                  minWidth: { xs: '250px', sm: '200px' },
                  width: { xs: '100%', sm: 'auto' },
                  maxWidth: { xs: '300px', sm: 'none' },
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                    boxShadow: theme.palette.shadows.colored.primaryHover,
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                🩺 Sign Up as Provider
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => handleChangeRoute('/register/hospitals')()}
                sx={{
                  borderRadius: '50px',
                  px: { xs: 4, sm: 5 },
                  py: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '1rem', sm: '1.2rem' },
                  fontWeight: 600,
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.primary.main,
                  border: `2px solid ${theme.palette.primary.light}`,
                  boxShadow: theme.palette.shadows.light,
                  textTransform: 'none',
                  minWidth: { xs: '250px', sm: '200px' },
                  width: { xs: '100%', sm: 'auto' },
                  maxWidth: { xs: '300px', sm: 'none' },
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light,
                    color: 'white',
                    boxShadow: theme.palette.shadows.colored.primary,
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                🏥 Sign Up as Hospital
              </Button>
            </Box>
          </Box>


        </Box>
      </Container>
      </Box>
    </Card>
  )
}

export default NewHeroSection
